
import { defineComponent, ref, computed, onMounted, onUnmounted, watch } from 'vue'
import WXQuotationWebSocket from '@/websocket/quotationWebsocket'

export default defineComponent({
  name: 'StockInfoCard',
  props: {
    isDark: { type: Boolean, default: true },
    symbol: { type: String, default: '' },
    name: { type: String, default: '' },
    market: { type: String, default: '' },
    lastPx: { type: Number, default: null },
    pxChangeRate: { type: Number, default: null },
  },
  setup(props) {
    const lastPrice = ref<number | null>(null)
    const preClosePrice = ref<number | null>(null)

    // 涨跌额 = LastPrice - PreClosePrice
    const riseAndFall = computed(() => {
      if (lastPrice.value == null || preClosePrice.value == null) return null
      return lastPrice.value - preClosePrice.value
    })

    // 涨跌幅 = (LastPrice - PreClosePrice) / PreClosePrice * 100
    const increase = computed(() => {
      if (lastPrice.value == null || preClosePrice.value == null || preClosePrice.value === 0) return null
      return ((lastPrice.value - preClosePrice.value) / preClosePrice.value) * 100
    })

    let ws: any = null

    const subscribe = () => {
      if (!props.symbol || !props.market) return
      if (!ws) {
        ws = new WXQuotationWebSocket()
        ws.connect()
        ws.on(WXQuotationWebSocket.EVENT.onOpen, () => {
          ws.safeSubscribeDYNA(props.symbol, props.market)
        })
        ws.on(WXQuotationWebSocket.EVENT.onMessage, (msg: any) => {
          const _data = msg?.QuoteData
          if (_data?.StatisticsData?.[0]) {
            preClosePrice.value = _data.StatisticsData[0].PreClosePrice ?? null
          }
          if (_data?.DynaData?.[0]) {
            lastPrice.value = _data.DynaData[0].LastPrice ?? null
          }
        })
      } else {
        ws.safeSubscribeDYNA(props.symbol, props.market)
      }
    }

    const unsubscribe = () => {
      if (ws) {
        ws.close()
        ws = null
      }
    }

    watch(() => [props.symbol, props.market], () => {
      unsubscribe()
      subscribe()
    }, { immediate: true })

    // 初始值用接口数据，WebSocket 到来后覆盖
    watch(() => props.lastPx, (v) => {
      if (v != null && lastPrice.value == null) lastPrice.value = v
    }, { immediate: true })

    onUnmounted(() => unsubscribe())

    const colorClass = computed(() => {
      if (riseAndFall.value == null) return ''
      return riseAndFall.value > 0 ? 'rise' : riseAndFall.value < 0 ? 'fall' : ''
    })

    const fmt2 = (v: number | null) => {
      if (v == null || v === 0) return '—'
      return Number(v).toFixed(2)
    }
    const fmtPct = (v: number | null) => {
      if (v == null) return '—'
      return Number(v).toFixed(2) + '%'
    }

    const displayPrice = computed(() => fmt2(lastPrice.value))
    const displayChange = computed(() => {
      if (riseAndFall.value == null || riseAndFall.value === 0) return '—'
      return (riseAndFall.value > 0 ? '+' : '') + Number(riseAndFall.value).toFixed(2)
    })
    const displayRate = computed(() => {
      const rate = increase.value != null ? increase.value : (props.pxChangeRate != null ? props.pxChangeRate * 100 : null)
      if (rate == null) return '—'
      return (rate > 0 ? '+' : '') + fmtPct(rate)
    })

    return { colorClass, displayPrice, displayChange, displayRate }
  }
})
